/**
 * SimEvent: everything the renderer, audio, HUD and announcer react to (ARCHITECTURE §4).
 *
 * Rules:
 *  - Every event has `type`, `tick` (the tick being stepped, i.e. `state.tick` BEFORE the increment) and
 *    `actor` (player index 0/1, or -1 for "the game").
 *  - The tuple (tick, type, actor) is unique per tick: the online client de-duplicates resimulated events with
 *    `eventKey`. If a tick could legitimately emit two events of the same type for the same actor, fold them
 *    WITHOUT losing information (e.g. POWER_GAIN carries a `reasons` bitmask and the summed `amount`).
 *  - Events are collected on every simulated tick, rollback resimulation included (see RollbackGame.step).
 *  - Events are facts about the sim, never instructions. Consumers must not read sim internals beyond the
 *    state they are given for rendering.
 *  - Events are allocated only when something happens (rare), never per tick in steady state.
 *  - Payload numbers use world units (yards) and SimState enums.
 */
import type { SpecialId } from '@hf/shared';
import type { BaitKey } from './enums';

interface Base<T extends string> {
  type: T;
  tick: number;
  /** Player index (0/1) the event is about, or -1 for game-level events. */
  actor: number;
}

/** Common position payload (where to spawn VFX / pan audio). */
interface At {
  x: number;
  y: number;
}

// ── Pre-snap & snap ────────────────────────────────────────────────────────────────────────────────────
/** New drive begins (offense = actor). */
export interface DriveStartEvent extends Base<'DRIVE_START'> {
  driveNumber: number;
  los: number;
  overtime: boolean;
}
/** A new down is lined up (HUD chyron). */
export interface DownStartEvent extends Base<'DOWN_START'> {
  down: number;
  distance: number;
  los: number;
  goalToGo: boolean;
}
export interface SnapEvent extends Base<'SNAP'>, At {
  /** True if the defense had Blitz ARMED: revealed now (whistle), its telegraph starts this tick. */
  blitz: boolean;
  /** Auto-snapped because the play clock expired. */
  auto: boolean;
  /** PlayCall declared with the snap (FG_TOSS = offense held ▼ while snapping; PAT_TOSS on a 1-pt try). */
  playCall: number;
}
export interface HardCountEvent extends Base<'HARD_COUNT'>, At {}
export interface OffsideEvent extends Base<'OFFSIDE'>, At {
  penaltyYards: number;
}
/** The rush count ended: the defender may cross. */
export interface RushGoEvent extends Base<'RUSH_GO'> {
  /**
   * timer = full count elapsed · offense_crossed = the offense (head, ball in its glove, or its pass) crossed the LOS
   * first · blitz = Blitz telegraph done · turnover = the defense caught the ball behind the LOS (interception
   * during the count: the return is live at once).
   */
  reason: 'timer' | 'offense_crossed' | 'blitz' | 'turnover';
}
export interface PlayClockWarningEvent extends Base<'PLAY_CLOCK_WARNING'> {
  secondsLeft: number;
}

// ── Classic format: kicks, halves ──────────────────────────────────────────────────────────────────────
/** A kickoff is lined up (actor = the kicker): opening kick, after a score, each half, the post-safety free kick. */
export interface KickoffStartEvent extends Base<'KICKOFF_START'> {
  los: number;
  half: number;
  /** Free kick after a safety (from the kicker's own 20). */
  safetyKick: boolean;
}
/** A kick is away (actor = the kicker). THROW is emitted on the same tick for the generic glove/launch effects. */
export interface KickEvent extends Base<'KICK'>, At {
  vx: number;
  vy: number;
  kind: 'kickoff' | 'onside' | 'punt';
  charge: number;
  /** Auto-kicked (play clock / KICK.kickoffAutoFrames). */
  auto: boolean;
}
/** The receiver secured a kick (actor = receiver): caught in the air, or picked up off the ground. */
export interface KickCaughtEvent extends Base<'KICK_CAUGHT'>, At {
  kind: 'kickoff' | 'onside' | 'punt';
  fromGround: boolean;
  /** Caught/picked up inside his own end zone (he may run it out or take a knee: hold ▼). */
  inEndZone: boolean;
}
/**
 * The KICKING player got to a free kick first (actor = kicker): an onside / kickoff past the 10-yard line or a
 * kick the receiver muffed (`downed` = false: he keeps the ball) — or he downed an untouched punt (`downed` =
 * true: the receiver's ball at that spot).
 */
export interface KickRecoveredEvent extends Base<'KICK_RECOVERED'>, At {
  kind: 'kickoff' | 'onside' | 'punt';
  downed: boolean;
}
/** Touchback (actor = the receiver who gets the ball at `los`). */
export interface TouchbackEvent extends Base<'TOUCHBACK'> {
  los: number;
}
/** Classic: a half ended (actor -1). Half 1 → half-time (RULES.halftimeFrames) then the second-half kickoff. */
export interface HalfEndEvent extends Base<'HALF_END'> {
  half: number;
  score: [number, number];
}

// ── Movement & feints ──────────────────────────────────────────────────────────────────────────────────
export interface JumpEvent extends Base<'JUMP'>, At {
  /** Carrier jump (hurdle attempt). */
  hurdle: boolean;
}
export interface LandEvent extends Base<'LAND'>, At {
  /** Impact speed (yd/s) for dust size / camera bump. */
  impact: number;
}
export interface DashEvent extends Base<'DASH'>, At {
  dir: number;
}
export interface FastFallEvent extends Base<'FAST_FALL'>, At {}
/** Fake-jump cancel (freeze → slam). */
export interface FakeCancelEvent extends Base<'FAKE_CANCEL'>, At {}
/** Stutter step (dash cancelled into a back-step). */
export interface StutterEvent extends Base<'STUTTER'>, At {}
/** Hesitation burst after stopping dead. */
export interface HesitationEvent extends Base<'HESITATION'>, At {}
export interface SpinEvent extends Base<'SPIN'>, At {}
/** Spin i-frames dodged a grab. */
export interface SpinDodgeEvent extends Base<'SPIN_DODGE'>, At {}
export interface StumbleEvent extends Base<'STUMBLE'>, At {}
export interface TruckStartEvent extends Base<'TRUCK_START'>, At {}
export interface StiffArmEvent extends Base<'STIFF_ARM'>, At {
  /** True if it connected with the defender. */
  hit: boolean;
}
export interface DiveStartEvent extends Base<'DIVE_START'>, At {}
export interface FakeDiveEvent extends Base<'FAKE_DIVE'>, At {}
export interface LeapStartEvent extends Base<'LEAP_START'>, At {}
/** A lunge (dive/leap/grab) hit nothing. */
export interface WhiffEvent extends Base<'WHIFF'>, At {
  kind: 'dive' | 'leap' | 'grab';
}
/**
 * A feint baited the opponent (rule per feint kind: ARCHITECTURE §5 "Bait rules"): "WHAT A FAKE!" (+POWER.bait).
 * actor = the faker. `bait` = FEINT_BAIT_KEYS[feintKind].
 */
export interface FakeSuccessEvent extends Base<'FAKE_SUCCESS'>, At {
  bait: BaitKey;
  victim: number;
}
/** A lunge touched the Mirage decoy: it pops (puff of smoke). actor = the decoy's owner; x/y = decoy position. */
export interface DecoyPopEvent extends Base<'DECOY_POP'>, At {
  /** The player whose lunge popped it. */
  by: number;
}
export interface StunEvent extends Base<'STUN'>, At {
  frames: number;
}

// ── Passing ────────────────────────────────────────────────────────────────────────────────────────────
export interface ThrowChargeEvent extends Base<'THROW_CHARGE'>, At {}
export interface ThrowEvent extends Base<'THROW'>, At {
  vx: number;
  vy: number;
  /** Charge fraction 0..1. */
  charge: number;
  lob: boolean;
  hailMary: boolean;
  /** Thrown from behind the LOS (legal forward pass / FG toss). */
  forwardPass: boolean;
}
export interface PumpFakeEvent extends Base<'PUMP_FAKE'>, At {}
export interface CatchEvent extends Base<'CATCH'>, At {
  /** Air yards from the release point. */
  airYards: number;
  /** Caught at full extension / contested. */
  contested: boolean;
}
export interface InterceptionEvent extends Base<'INTERCEPTION'>, At {
  thrower: number;
}
/** Pass broken up by glove or head. */
export interface SwatEvent extends Base<'SWAT'>, At {
  by: 'glove' | 'head';
}
export interface IncompleteEvent extends Base<'INCOMPLETE'>, At {}
export interface BallBounceEvent extends Base<'BALL_BOUNCE'>, At {
  speed: number;
}
/** Ball hit a goalpost (DOINK). */
export interface PostHitEvent extends Base<'POST_HIT'>, At {
  part: 'crossbar' | 'upright' | 'stanchion';
}

// ── Contact ────────────────────────────────────────────────────────────────────────────────────────────
/**
 * Carrier tackled. actor = tackler. `counter` names the counter-square read that decided it
 * ('none' = plain tackle).
 */
export interface TackleEvent extends Base<'TACKLE'>, At {
  kind: 'dive' | 'leap' | 'grab';
  carrier: number;
  counter: 'none' | 'leap_beats_hurdle' | 'dive_beats_truck';
  /** Spot of the tackle relative to the LOS, in offense yards. */
  yards: number;
}
/** Truck beat a Leap (carrier ran under; defender knocked aside). actor = carrier. */
export interface TruckBreakEvent extends Base<'TRUCK_BREAK'>, At {
  defender: number;
}
/** Hurdle (airborne carrier) cleared a Dive. actor = carrier. */
export interface HurdleClearEvent extends Base<'HURDLE_CLEAR'>, At {
  defender: number;
}
/** Bulldozer broke a grounded tackle. actor = carrier. */
export interface BulldozeEvent extends Base<'BULLDOZE'>, At {
  defender: number;
}
/** Whole-sim freeze started (render: impact frame, shake). */
export interface HitStopEvent extends Base<'HIT_STOP'>, At {
  frames: number;
  cause:
    | 'tackle'
    | 'counter'
    | 'catch'
    | 'interception'
    | 'swat'
    | 'truck'
    | 'stiff_arm'
    | 'bulldoze'
    | 'fake'
    | 'touchdown'
    | 'post';
}

// ── Results & scoring ──────────────────────────────────────────────────────────────────────────────────
/** The play is over (ball dead). Always emitted once per play, after the specific result event(s). */
export interface PlayEndEvent extends Base<'PLAY_END'> {
  /** PlayResultKind. */
  result: number;
  yards: number;
  spotX: number;
}
export interface FirstDownEvent extends Base<'FIRST_DOWN'> {
  los: number;
}
export interface BigPlayEvent extends Base<'BIG_PLAY'> {
  yards: number;
}
export interface TouchdownEvent extends Base<'TOUCHDOWN'>, At {
  /** Interception returned for a TD. */
  pickSix: boolean;
  /** Classic: a kick returned for a TD (or a free kick recovered by the kicker in the end zone). */
  kickReturn?: boolean;
  score: [number, number];
}
/** Only a flight with `ball.fgAttempt` can produce FIELD_GOAL_GOOD / FIELD_GOAL_MISS. */
export interface FieldGoalGoodEvent extends Base<'FIELD_GOAL_GOOD'>, At {
  distance: number;
  /** PAT toss rather than a field goal. */
  pat: boolean;
  score: [number, number];
}
export interface FieldGoalMissEvent extends Base<'FIELD_GOAL_MISS'>, At {
  distance: number;
  pat: boolean;
  /** short = hit the ground first · over = above the upright tops · post = off a post then dead · wide = below the crossbar. */
  reason: 'short' | 'over' | 'post' | 'wide';
}
export interface SafetyEvent extends Base<'SAFETY'>, At {
  score: [number, number];
}
export interface SackEvent extends Base<'SACK'>, At {
  yards: number;
}
export interface TurnoverOnDownsEvent extends Base<'TURNOVER_ON_DOWNS'> {}
export interface ConversionStartEvent extends Base<'CONVERSION_START'> {}
export interface ConversionChoiceEvent extends Base<'CONVERSION_CHOICE'> {
  choice: 'toss' | 'two_point';
}
export interface ConversionResultEvent extends Base<'CONVERSION_RESULT'> {
  good: boolean;
  points: number;
  score: [number, number];
}
export interface OvertimeStartEvent extends Base<'OVERTIME_START'> {
  round: number;
  /** The final overtime round (RULES.maxOvertimeRounds): still tied after it, yards decide (GAME_OVER tiebreak). */
  lastRound?: boolean;
}
export interface GameOverEvent extends Base<'GAME_OVER'> {
  /** actor = winner. */
  score: [number, number];
  /** Set when the score was still tied after the last overtime round: the winner had more yards (statYards). */
  tiebreak?: 'yards';
}
export interface CelebrateEvent extends Base<'CELEBRATE'>, At {}

// ── Resources & specials ───────────────────────────────────────────────────────────────────────────────
export interface StaminaEmptyEvent extends Base<'STAMINA_EMPTY'> {}
/** A feint was attempted with insufficient stamina ("MUST BE REAL"). */
export interface StaminaDeniedEvent extends Base<'STAMINA_DENIED'> {}
export interface PowerFullEvent extends Base<'POWER_FULL'> {}
/** One per actor per tick: simultaneous gains are summed in `amount` and OR-ed into `reasons` (PowerReason bits). */
export interface PowerGainEvent extends Base<'POWER_GAIN'> {
  amount: number;
  reasons: number;
}
export interface SpecialTelegraphEvent extends Base<'SPECIAL_TELEGRAPH'>, At {
  special: SpecialId;
}
export interface SpecialActivateEvent extends Base<'SPECIAL_ACTIVATE'>, At {
  special: SpecialId;
}
export interface SpecialEndEvent extends Base<'SPECIAL_END'> {
  special: SpecialId;
  /**
   * Consumed by its effect (e.g. Bulldozer broke a tackle) vs timed out / the play ended first (the power stays
   * spent either way; emitted at PLAY_END for a special still in TELEGRAPH/ACTIVE).
   */
  consumed: boolean;
}

export type SimEvent =
  | DriveStartEvent
  | DownStartEvent
  | SnapEvent
  | HardCountEvent
  | OffsideEvent
  | RushGoEvent
  | PlayClockWarningEvent
  | KickoffStartEvent
  | KickEvent
  | KickCaughtEvent
  | KickRecoveredEvent
  | TouchbackEvent
  | HalfEndEvent
  | JumpEvent
  | LandEvent
  | DashEvent
  | FastFallEvent
  | FakeCancelEvent
  | StutterEvent
  | HesitationEvent
  | SpinEvent
  | SpinDodgeEvent
  | StumbleEvent
  | TruckStartEvent
  | StiffArmEvent
  | DiveStartEvent
  | FakeDiveEvent
  | LeapStartEvent
  | WhiffEvent
  | FakeSuccessEvent
  | DecoyPopEvent
  | StunEvent
  | ThrowChargeEvent
  | ThrowEvent
  | PumpFakeEvent
  | CatchEvent
  | InterceptionEvent
  | SwatEvent
  | IncompleteEvent
  | BallBounceEvent
  | PostHitEvent
  | TackleEvent
  | TruckBreakEvent
  | HurdleClearEvent
  | BulldozeEvent
  | HitStopEvent
  | PlayEndEvent
  | FirstDownEvent
  | BigPlayEvent
  | TouchdownEvent
  | FieldGoalGoodEvent
  | FieldGoalMissEvent
  | SafetyEvent
  | SackEvent
  | TurnoverOnDownsEvent
  | ConversionStartEvent
  | ConversionChoiceEvent
  | ConversionResultEvent
  | OvertimeStartEvent
  | GameOverEvent
  | CelebrateEvent
  | StaminaEmptyEvent
  | StaminaDeniedEvent
  | PowerFullEvent
  | PowerGainEvent
  | SpecialTelegraphEvent
  | SpecialActivateEvent
  | SpecialEndEvent;

export type SimEventType = SimEvent['type'];
export type SimEventOf<T extends SimEventType> = Extract<SimEvent, { type: T }>;

/** Every event type, for exhaustive switch tests and tooling. Keep in sync with the union (a test checks). */
export const SIM_EVENT_TYPES = [
  'DRIVE_START',
  'DOWN_START',
  'SNAP',
  'HARD_COUNT',
  'OFFSIDE',
  'RUSH_GO',
  'PLAY_CLOCK_WARNING',
  'KICKOFF_START',
  'KICK',
  'KICK_CAUGHT',
  'KICK_RECOVERED',
  'TOUCHBACK',
  'HALF_END',
  'JUMP',
  'LAND',
  'DASH',
  'FAST_FALL',
  'FAKE_CANCEL',
  'STUTTER',
  'HESITATION',
  'SPIN',
  'SPIN_DODGE',
  'STUMBLE',
  'TRUCK_START',
  'STIFF_ARM',
  'DIVE_START',
  'FAKE_DIVE',
  'LEAP_START',
  'WHIFF',
  'FAKE_SUCCESS',
  'DECOY_POP',
  'STUN',
  'THROW_CHARGE',
  'THROW',
  'PUMP_FAKE',
  'CATCH',
  'INTERCEPTION',
  'SWAT',
  'INCOMPLETE',
  'BALL_BOUNCE',
  'POST_HIT',
  'TACKLE',
  'TRUCK_BREAK',
  'HURDLE_CLEAR',
  'BULLDOZE',
  'HIT_STOP',
  'PLAY_END',
  'FIRST_DOWN',
  'BIG_PLAY',
  'TOUCHDOWN',
  'FIELD_GOAL_GOOD',
  'FIELD_GOAL_MISS',
  'SAFETY',
  'SACK',
  'TURNOVER_ON_DOWNS',
  'CONVERSION_START',
  'CONVERSION_CHOICE',
  'CONVERSION_RESULT',
  'OVERTIME_START',
  'GAME_OVER',
  'CELEBRATE',
  'STAMINA_EMPTY',
  'STAMINA_DENIED',
  'POWER_FULL',
  'POWER_GAIN',
  'SPECIAL_TELEGRAPH',
  'SPECIAL_ACTIVATE',
  'SPECIAL_END',
] as const satisfies readonly SimEventType[];

/** De-duplication key for rollback resimulation: `${tick}:${type}:${actor}`. */
export const eventKey = (e: SimEvent): string => `${e.tick}:${e.type}:${e.actor}`;

// Compile-time check: SIM_EVENT_TYPES lists every member of the union.
type MissingEventTypes = Exclude<SimEventType, (typeof SIM_EVENT_TYPES)[number]>;
export const SIM_EVENT_TYPES_EXHAUSTIVE: [MissingEventTypes] extends [never] ? true : MissingEventTypes =
  true;
